import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { IProfileRow } from "graphql/queries/filterProfilesPaginated";

const fmt = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—");

function Field({ label, value }: { label: string; value: any }): JSX.Element {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {label}
      </MDTypography>
      <MDTypography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </MDTypography>
    </MDBox>
  );
}

function SocialLink({ label, url }: { label: string; url?: string }): JSX.Element {
  return (
    <MDBox mb={1.5}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {label}
      </MDTypography>
      {url ? (
        <MDTypography variant="body2">
          <Link href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </Link>
        </MDTypography>
      ) : (
        <MDTypography variant="body2">—</MDTypography>
      )}
    </MDBox>
  );
}

// Read-only detail view of a chat profile.
function ProfileDetailModal({
  profile,
  onClose,
}: {
  profile: IProfileRow;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <DialogTitle>
          <MDTypography fontSize="16px" fontWeight="medium">
            Profile details
          </MDTypography>
        </DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </MDBox>
      <Divider sx={{ my: 0 }} />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Field label="Channel" value={profile.channel} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Channel User" value={profile.channelUserId} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Display Name" value={profile.displayName} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Type" value={profile.profileType} />
          </Grid>
          <Grid item xs={6}>
            <Field
              label="Connected Account"
              value={
                profile.connectedUser
                  ? `${profile.connectedUser.email}${
                      profile.connectedUser.fullName
                        ? ` (${profile.connectedUser.fullName})`
                        : ""
                    }`
                  : "Not connected"
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Field label="Connected At" value={fmt(profile.connectedAt)} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Terms Accepted" value={fmt(profile.termsAcceptedAt)} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Created" value={fmt(profile.createdAt)} />
          </Grid>
        </Grid>
        <Divider />
        <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
          Socials
        </MDTypography>
        <MDBox mt={1}>
          <SocialLink label="Website" url={profile.socials?.website} />
          <SocialLink label="LinkedIn" url={profile.socials?.linkedin} />
          <SocialLink label="Instagram" url={profile.socials?.instagram} />
          <SocialLink label="Facebook" url={profile.socials?.facebook} />
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

export default ProfileDetailModal;
